import { IonContent, IonPage } from "@ionic/react";
import { useCallback, useEffect } from "react";
import ReactGA from "react-ga4";
import { Helmet } from "react-helmet-async";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { SwiperSlide } from "swiper/react";

import ActivateButton from "@components/ActivateButton";
import styles from "@components/ActivatePage.module.css";
import Slides from "@components/Slides";
import Icon from "@components/text/Icon";
import ButtonLink from "@components/typography/ButtonLink";
import useCreateActivationAnalyticsEvent from "@hooks/mutations/useCreateActivationAnalyticsEvent";
import { ClientTopicOfInterestEnum } from "@typing/Generated";
import { ImageName, imageUrl } from "@utils/imageUtils";
import storage from "@utils/storageUtils";

const Reason = ({ reasonName }: { reasonName: string }) => (
  <div className={styles.reason}>
    <div className={styles.reasonName}>
      <FormattedMessage id={`components.ActivatePage.features.${reasonName}.heading`} />
    </div>
    <div className={styles.reasonDescription}>
      <FormattedMessage id={`components.ActivatePage.features.${reasonName}.body`} />
    </div>
  </div>
);

const ActivatePage = () => {
  const intl = useIntl();
  const history = useHistory();

  const createActivationAnalyticsEvent = useCreateActivationAnalyticsEvent({
    action: "visit",
    topic: ClientTopicOfInterestEnum.UNKNOWN
  });

  useEffect(() => {
    createActivationAnalyticsEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkForTopic = (topic: ClientTopicOfInterestEnum) => {
    if (topic === ClientTopicOfInterestEnum.UNKNOWN) return "/sign-up";
    return `/activate/${topic.toLowerCase()}`;
  };

  const generateHandleTopicClick = useCallback(
    (topic: ClientTopicOfInterestEnum) => () => {
      if (import.meta.env.VITE_GOOGLE_ANALYTICS_ID) {
        ReactGA.event("activate-click", { topicOfInterest: topic });
      }
      storage.set("topicOfInterest", topic).then(() => {
        history.push(linkForTopic(topic));
      });
    },
    [history]
  );

  return (
    <IonPage className={styles.page}>
      <Helmet>
        <title>{intl.formatMessage({ id: "pageTitles.unauthenticated.activate.show" })}</title>
        <link href="https://fonts.googleapis.com" rel="preconnect" />
        <link crossOrigin="" href="https://fonts.gstatic.com" rel="preconnect" />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Mulish&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <IonContent>
        <div className={styles.headerBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.grayceLogo}>
              <img alt={intl.formatMessage({ id: "accessibility.altTags.purpleLogo" })} src={imageUrl("logoPurple")} />
            </div>
            <div className={styles.headerTopRow}>
              <div className={styles.headerIntro}>
                <div className={`${styles.allCapsHeading} ${styles.getStartedDesktop}`}>
                  <FormattedMessage id="components.ActivatePage.getStartedDesktop" />
                </div>
                <div className={`${styles.allCapsHeading} ${styles.getStartedMobile}`}>
                  <FormattedMessage id="components.ActivatePage.getStartedMobile" />
                </div>
                <div className={styles.hugeText}>
                  <FormattedMessage id="components.ActivatePage.weHelpLightenTheLoad" />
                </div>
                <ActivateButton className={styles.activateButton} topic={ClientTopicOfInterestEnum.UNKNOWN} />
              </div>
              <div className={styles.headerFeatureImage}>
                <img
                  alt={intl.formatMessage({ id: "accessibility.altTags.activateFeatureFamily" })}
                  src={imageUrl("activateFeatureFamily")}
                />
              </div>
            </div>

            <div className={styles.headerTopics}>
              <div className={styles.allCapsHeading}>
                <FormattedMessage id="components.ActivatePage.whatSituation" />
              </div>

              <div className={styles.headerTopicsRow}>
                <ButtonLink
                  className={`${styles.headerTopic} ${styles.headerTopic1}`}
                  onClick={generateHandleTopicClick(ClientTopicOfInterestEnum.PREGNANCY)}
                  stylized={false}
                >
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.activateTopics.pregnancy" })}
                    className={styles.headerTopicHoverImage}
                    src={imageUrl("activateTopicPregnancy")}
                  />
                  <div className={styles.headerTopicText}>
                    <FormattedMessage id="components.ActivatePage.topics.pregnancy" />
                  </div>
                  <div className={styles.headerTopicArrow}>
                    <Icon color="white" icon="circleArrowRight" />
                  </div>
                </ButtonLink>
                <ButtonLink
                  className={`${styles.headerTopic} ${styles.headerTopic2}`}
                  onClick={generateHandleTopicClick(ClientTopicOfInterestEnum.CHILD)}
                  stylized={false}
                >
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.activateTopics.child" })}
                    className={styles.headerTopicHoverImage}
                    src={imageUrl("activateTopicChild")}
                  />
                  <div className={styles.headerTopicText}>
                    <FormattedMessage id="components.ActivatePage.topics.child" />
                  </div>
                  <div className={styles.headerTopicArrow}>
                    <Icon color="white" icon="circleArrowRight" />
                  </div>
                </ButtonLink>
                <ButtonLink
                  className={`${styles.headerTopic} ${styles.headerTopic3}`}
                  onClick={generateHandleTopicClick(ClientTopicOfInterestEnum.ADULT)}
                  stylized={false}
                >
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.activateTopics.adult" })}
                    className={styles.headerTopicHoverImage}
                    src={imageUrl("activateTopicAdult")}
                  />
                  <div className={styles.headerTopicText}>
                    <FormattedMessage id="components.ActivatePage.topics.adult" />
                  </div>
                  <div className={styles.headerTopicArrow}>
                    <Icon color="white" icon="circleArrowRight" />
                  </div>
                </ButtonLink>
                <ButtonLink
                  className={`${styles.headerTopic} ${styles.headerTopic4}`}
                  onClick={generateHandleTopicClick(ClientTopicOfInterestEnum.AGING)}
                  stylized={false}
                >
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.activateTopics.aging" })}
                    className={styles.headerTopicHoverImage}
                    src={imageUrl("activateTopicAging")}
                  />
                  <div className={styles.headerTopicText}>
                    <FormattedMessage id="components.ActivatePage.topics.aging" />
                  </div>
                  <div className={styles.headerTopicArrow}>
                    <Icon color="white" icon="circleArrowRight" />
                  </div>
                </ButtonLink>
                <ButtonLink
                  className={`${styles.headerTopic} ${styles.headerTopic5}`}
                  onClick={generateHandleTopicClick(ClientTopicOfInterestEnum.GRIEVING)}
                  stylized={false}
                >
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.activateTopics.grieving" })}
                    className={styles.headerTopicHoverImage}
                    src={imageUrl("activateTopicGrieving")}
                  />
                  <div className={styles.headerTopicText}>
                    <FormattedMessage id="components.ActivatePage.topics.grieving" />
                  </div>
                  <div className={styles.headerTopicArrow}>
                    <Icon color="white" icon="circleArrowRight" />
                  </div>
                </ButtonLink>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.reasonsBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.reasonsSlides}>
              <Slides autoplay={{ delay: 7000 }} loop slidesPerView={1}>
                <SwiperSlide className={styles.slide}>
                  <Reason reasonName="planAhead" />
                </SwiperSlide>
                <SwiperSlide className={styles.slide}>
                  <Reason reasonName="saveTime" />
                </SwiperSlide>
                <SwiperSlide className={styles.slide}>
                  <Reason reasonName="research" />
                </SwiperSlide>
              </Slides>
            </div>
            <div className={styles.reasonsRow}>
              <Reason reasonName="planAhead" />
              <Reason reasonName="saveTime" />
              <Reason reasonName="research" />
            </div>
          </div>
        </div>

        <div className={styles.featuresBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.featuresTopRow}>
              <div className={styles.featuresFeatureImage}>
                <img
                  alt={intl.formatMessage({ id: "accessibility.altTags.activateFeatureDogWash" })}
                  src={imageUrl("activateFeatureDogWash")}
                />
              </div>
              <div className={styles.featuresIntro}>
                <div className={styles.allCapsHeading}>
                  <FormattedMessage id="components.ActivatePage.getPeaceOfMind" />
                </div>
                <div className={styles.hugeText}>
                  <FormattedMessage id="components.ActivatePage.weStepIn" />
                </div>
                <div className={styles.featuresExpertGuidance}>
                  <FormattedMessage id="components.ActivatePage.expertGuidance" />
                </div>
                <ActivateButton className={styles.activateButton} topic={ClientTopicOfInterestEnum.UNKNOWN} />
                <div className={styles.featuresRatingAndComplianceRow}>
                  <div className={styles.featuresRatingColumn}>
                    <div>
                      <img
                        alt={intl.formatMessage({ id: "accessibility.altTags.activateStars" })}
                        src={imageUrl("activateFeatureStarRating")}
                      />
                    </div>
                    <div>
                      <FormattedMessage id="components.ActivatePage.memberRating" />
                    </div>
                  </div>
                  <div>
                    <img
                      alt={intl.formatMessage({ id: "accessibility.altTags.activateHipaa" })}
                      src={imageUrl("activateFeatureHipaa")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.testimonialsBlock}>
          <div className={styles.contentHolder}>
            <div className={`${styles.hugeText} ${styles.centered} ${styles.testimonialHeading}`}>
              <FormattedMessage id="components.ActivatePage.whatOurMembersAreSaying" />
            </div>

            <Slides autoplay={{ delay: 7000 }} loop slidesPerView={1}>
              {["aging", "yourself", "adult"].map(testimonial => (
                <SwiperSlide className={styles.slide} key={testimonial}>
                  <div className={styles.testimonial}>
                    <img
                      alt={intl.formatMessage({ id: "accessibility.altTags.entrepeneurLogo" })}
                      className={styles.testimonialImage}
                      src={imageUrl(
                        `activateTestimonial${testimonial[0].toUpperCase() + testimonial.substring(1)}` as ImageName
                      )}
                    />
                    <div className={styles.testimonialCopy}>
                      <div className={styles.testimonialHeadline}>
                        <FormattedMessage id={`components.ActivatePage.testimonials.${testimonial}.headline`} />
                      </div>
                      <div className={styles.testimonialButton}>
                        <FormattedMessage id={`components.ActivatePage.testimonials.${testimonial}.button`} />
                      </div>
                      <div className={styles.testimonialBody}>
                        <FormattedMessage id={`components.ActivatePage.testimonials.${testimonial}.body`} />
                      </div>
                      <div>
                        <div className={styles.testimonialName}>
                          <FormattedMessage id={`components.ActivatePage.testimonials.${testimonial}.name`} />
                        </div>
                        <div className={styles.testimonialLocation}>
                          <FormattedMessage id={`components.ActivatePage.testimonials.${testimonial}.location`} />
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Slides>
          </div>
        </div>

        <div className={styles.logosBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.logoWrapper}>
              <div className={styles.allCapsHeading}>
                <FormattedMessage id="components.ActivatePage.asFeaturedIn" />
              </div>
              <div className={styles.logoRow}>
                <div className={styles.logo}>
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.entrepeneurLogo" })}
                    src={imageUrl("activateLogoEntrepeneur")}
                  />
                </div>
                <div className={styles.logo}>
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.forbesLogo" })}
                    src={imageUrl("activateLogoForbes")}
                  />
                </div>
                <div className={styles.logo}>
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.timeLogo" })}
                    src={imageUrl("activateLogoTime")}
                  />
                </div>
                <div className={styles.logo}>
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.usNewsLogo" })}
                    src={imageUrl("activateLogoUsnews")}
                  />
                </div>
                <div className={styles.logo}>
                  <img
                    alt={intl.formatMessage({ id: "accessibility.altTags.womensHealthLogo" })}
                    src={imageUrl("activateLogoWomensHealth")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footerBlock}>
          <div className={styles.contentHolder}>
            <div className={styles.hugeText}>
              <FormattedMessage id="components.ActivatePage.getSupport" />
            </div>
            <div className={styles.footerButtonWrapper}>
              <ActivateButton className={styles.activateButton} topic={ClientTopicOfInterestEnum.UNKNOWN} />
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ActivatePage;
